import React, { useEffect, useState, useRef } from 'react';
import { Card, Typography, Button, Empty, Avatar, Tag, Popconfirm, message } from 'antd';
import { MessageOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { actionApi, ActionValidationError } from '../../../utils/actionApi';
import { getSuccessMessage } from '../../../utils/actionMessageTranslator';
import { CommentsSkeleton } from '../../../components/SkeletonLoaders';
import MentionTextArea, { convertMentionsToEmails, renderContentWithMentions } from '../../../components/MentionTextArea';
import '../../../components/MentionTextArea.css';

const { Title, Text } = Typography;

export default function ActionCommentsTab({ actionId, primaryButtonClass, isActive = false }) {
  const [comments, setComments] = useState([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [commentContent, setCommentContent] = useState('');
  const [commentParentId, setCommentParentId] = useState(null);
  const [commentSubmitting, setCommentSubmitting] = useState(false);
  const [commentDeletingId, setCommentDeletingId] = useState(null);
  const [isMentionSyncing, setIsMentionSyncing] = useState(false);
  
  // Ref برای دسترسی به MentionTextArea
  const mentionRef = useRef(null);
  
  // State برای ذخیره mapping منشن‌ها (به عنوان backup، اما ref اصلی است)
  const [mentionMappings, setMentionMappings] = useState([]);

  const loadComments = async () => {
    if (!actionId) return;
    setCommentsLoading(true);
    try {
      const res = await actionApi.getActionComments(actionId);
      // نمایش پاسخ با متن فارسی قابل خواندن
      console.log('💬 Comments Response (Parsed):', res);
      console.log('💬 Comments Response (Formatted):', JSON.stringify(res, null, 2));
      
      const list = res?.data || res?.data?.data || [];
      // فقط کامنت‌های اصلی (parent_id === null) را نگه می‌داریم
      const rootComments = Array.isArray(list) 
        ? list.filter(c => !c.parent_id || c.parent_id === null)
        : [];
      setComments(rootComments);
    } catch (e) {
      console.error(e);
      message.error('خطا در دریافت نظرات');
    } finally {
      setCommentsLoading(false);
    }
  };

  useEffect(() => {
    if (isActive && actionId) {
      loadComments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionId, isActive]);

  const onSubmitComment = async () => {
    // بررسی اینکه آیا sync در حال انجام است
    if (mentionRef.current?.isSyncing && mentionRef.current.isSyncing()) {
      message.warning('لطفاً صبر کنید تا همگام‌سازی کاربران منشن شده تکمیل شود...');
      return;
    }

    setCommentSubmitting(true);
    try {
      const rawContent = (commentContent || '').trim();
      if (!rawContent) {
        message.error('متن نظر الزامی است');
        return;
      }
      if (rawContent.length > 5000) {
        message.error('متن نظر حداکثر ۵۰۰۰ کاراکتر است');
        return;
      }

      // دریافت آخرین mappings از ref (این مطمئن‌ترین روش است)
      const currentMappings = mentionRef.current?.getMappings() || mentionMappings;
      
      console.log('=== Submit Comment Debug ===');
      console.log('Raw content:', rawContent);
      console.log('Mappings from ref:', mentionRef.current?.getMappings());
      console.log('Mappings from state:', mentionMappings);
      console.log('Using mappings:', currentMappings);

      // بررسی اینکه آیا همه mapping ها username دارند
      const mappingsWithoutUsername = currentMappings.filter(m => !m.username);
      if (mappingsWithoutUsername.length > 0) {
        console.warn('⚠️ Some mappings do not have username:', mappingsWithoutUsername);
      }

      // تبدیل منشن‌ها از @نام به @username برای ارسال به بک‌اند
      const content = convertMentionsToEmails(rawContent, currentMappings);
      
      console.log('=== Conversion Result ===');
      console.log('Before:', rawContent);
      console.log('After:', content);

      const payload = { content };
      if (commentParentId) payload.parent_id = commentParentId;
      
      console.log('Final payload:', payload);
      
      const res = await actionApi.createActionComment(actionId, payload);
      if (res?.success) {
        message.success(getSuccessMessage(res.message, 'نظر با موفقیت ثبت شد'));
        setCommentContent('');
        setCommentParentId(null);
        setMentionMappings([]);
        // پاک کردن mappings از ref
        mentionRef.current?.clearMappings();
        await loadComments();
      } else {
        message.error(res?.message || 'خطا در ثبت نظر');
      }
    } catch (error) {
      console.error(error);
      if (error instanceof ActionValidationError) {
        const contentErr = error.errors?.content?.[0];
        const parentErr = error.errors?.parent_id?.[0];
        message.error(contentErr || parentErr || error.message || 'خطا در اعتبارسنجی نظر');
      } else {
        message.error(error.message || 'خطا در ثبت نظر');
      }
    } finally {
      setCommentSubmitting(false);
    }
  };

  const onDeleteComment = async (commentId) => {
    setCommentDeletingId(commentId);
    try {
      const res = await actionApi.deleteActionComment(actionId, commentId);
      if (res?.success) {
        message.success(getSuccessMessage(res.message, 'نظر حذف شد'));
        await loadComments();
      } else {
        message.error(res?.message || 'خطا در حذف نظر');
      }
    } catch (error) {
      console.error(error);
      message.error(error.message || 'خطا در حذف نظر');
    } finally {
      setCommentDeletingId(null);
    }
  };

  // کامپوننت recursive برای نمایش replies
  const CommentReplies = ({ replies, depth = 0 }) => {
    if (!replies || !Array.isArray(replies) || replies.length === 0) {
      return null;
    }

    const indentLevel = depth * 24; // 24px برای هر سطح

    return (
      <div 
        className="mt-2 space-y-2"
        style={{ 
          marginLeft: `${indentLevel}px`,
          paddingLeft: '16px',
          borderLeft: '2px solid #d9d9d9',
        }}
      >
        {replies.map((reply) => (
          <div key={reply.id} className="flex flex-col">
            <div
              className={`p-3 rounded-lg border border-gray-200 dark:border-gray-700 flex flex-col gap-2 ${
                depth === 0 
                  ? 'bg-gray-50 dark:bg-gray-800/50' 
                  : depth === 1
                  ? 'bg-gray-100 dark:bg-gray-800/30'
                  : 'bg-gray-200 dark:bg-gray-800/20'
              }`}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <Avatar 
                    size="small" 
                    style={{ 
                      backgroundColor: depth === 0 ? '#52c41a' : depth === 1 ? '#faad14' : '#ff7875',
                      fontSize: '11px'
                    }}
                  >
                    {String(reply.user?.name || '').slice(0, 1).toUpperCase() || '?'}
                  </Avatar>
                  <div className="flex items-center gap-1.5">
                    <ArrowLeftOutlined 
                      style={{ fontSize: '11px', color: '#8c8c8c' }} 
                    />
                    <div className="flex flex-col">
                      <Text style={{ fontFamily: 'CustomFont', fontSize: '12px', fontWeight: 500 }}>
                        {reply.user?.name || 'کاربر ناشناس'}
                      </Text>
                      {reply.created_at && (
                        <Text
                          type="secondary"
                          style={{ fontFamily: 'CustomFont', fontSize: 10 }}
                        >
                          {new Date(reply.created_at).toLocaleString('fa-IR')}
                        </Text>
                      )}
                    </div>
                  </div>
                </div>
                {reply.is_edited && (
                  <Tag color="default" style={{ fontFamily: 'CustomFont', fontSize: 9 }}>
                    ویرایش شده
                  </Tag>
                )}
              </div>
              <Text 
                style={{ 
                  fontFamily: 'CustomFont', 
                  fontSize: '12px', 
                  lineHeight: '1.5',
                  color: '#595959'
                }}
              >
                {renderContentWithMentions(reply.content)}
              </Text>
              <div className="mt-1 flex justify-end gap-2">
                <Button
                  type="link"
                  size="small"
                  onClick={() => setCommentParentId(reply.id)}
                  style={{ fontFamily: 'CustomFont', padding: '0 6px', fontSize: '11px' }}
                >
                  پاسخ
                </Button>
                <Popconfirm
                  title="حذف پاسخ"
                  description="آیا از حذف این پاسخ مطمئن هستید؟"
                  okText="حذف"
                  cancelText="انصراف"
                  okButtonProps={{ danger: true }}
                  onConfirm={() => onDeleteComment(reply.id)}
                >
                  <Button
                    type="link"
                    size="small"
                    danger
                    loading={commentDeletingId === reply.id}
                    style={{ fontFamily: 'CustomFont', padding: '0 6px', fontSize: '11px' }}
                  >
                    حذف
                  </Button>
                </Popconfirm>
              </div>
            </div>
            
            {/* نمایش nested replies */}
            {reply.replies && Array.isArray(reply.replies) && reply.replies.length > 0 && (
              <CommentReplies replies={reply.replies} depth={depth + 1} />
            )}
          </div>
        ))}
      </div>
    );
  };

  return (
    <Card style={{ borderRadius: 16 }} bodyStyle={{ padding: 16 }}>
      <div>
        <Title level={5} style={{ margin: 0, fontFamily: 'CustomFont' }}>
          نظرات اقدام
        </Title>
        <Text type="secondary" style={{ fontFamily: 'CustomFont' }}>
          ثبت و مشاهده نظرات مرتبط با این اقدام
        </Text>
      </div>

      <div className="mt-4">
        {commentsLoading ? (
          <CommentsSkeleton count={3} />
        ) : comments.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <span style={{ fontFamily: 'CustomFont' }}>
                هنوز نظری ثبت نشده است
              </span>
            }
          />
        ) : (
          <div className="space-y-4">
            {comments.map((c) => (
              <div key={c.id} className="flex flex-col">
                {/* کامنت اصلی */}
                <div className="p-4 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 flex flex-col gap-2">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <Avatar size="default" style={{ backgroundColor: '#1890ff' }}>
                        {String(c.user?.name || '').slice(0, 1).toUpperCase() || '?'}
                      </Avatar>
                      <div className="flex flex-col">
                        <Text style={{ fontFamily: 'CustomFont', fontWeight: 500 }}>
                          {c.user?.name || 'کاربر ناشناس'}
                        </Text>
                        {c.created_at && (
                          <Text
                            type="secondary"
                            style={{ fontFamily: 'CustomFont', fontSize: 12 }}
                          >
                            {new Date(c.created_at).toLocaleString('fa-IR')}
                          </Text>
                        )}
                      </div>
                    </div>
                    {c.is_edited && (
                      <Tag color="default" style={{ fontFamily: 'CustomFont', fontSize: 11 }}>
                        ویرایش شده
                      </Tag>
                    )}
                  </div>
                  <Text style={{ fontFamily: 'CustomFont', fontSize: '14px', lineHeight: '1.6' }}>
                    {renderContentWithMentions(c.content)}
                  </Text>
                  <div className="mt-2 flex justify-end gap-2">
                    <Button
                      type="link"
                      size="small"
                      onClick={() => setCommentParentId(c.id)}
                      style={{ fontFamily: 'CustomFont', padding: '0 8px' }}
                    >
                      پاسخ
                    </Button>
                    <Popconfirm
                      title="حذف نظر"
                      description="آیا از حذف این نظر مطمئن هستید؟"
                      okText="حذف"
                      cancelText="انصراف"
                      okButtonProps={{ danger: true }}
                      onConfirm={() => onDeleteComment(c.id)}
                    >
                      <Button
                        type="link"
                        size="small"
                        danger
                        loading={commentDeletingId === c.id}
                        style={{ fontFamily: 'CustomFont', padding: '0 8px' }}
                      >
                        حذف
                      </Button>
                    </Popconfirm>
                  </div>
                </div>

                {/* Reply ها */}
                {c.replies && Array.isArray(c.replies) && c.replies.length > 0 && (
                  <CommentReplies replies={c.replies} depth={0} />
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-800">
        {commentParentId && (
          <div className="mb-2 flex items-center justify-between">
            <Text type="secondary" style={{ fontFamily: 'CustomFont' }}>
              در حال پاسخ به نظر شماره #{commentParentId}
            </Text>
            <Button
              type="text"
              size="small"
              onClick={() => setCommentParentId(null)}
              style={{ fontFamily: 'CustomFont' }}
            >
              لغو پاسخ
            </Button>
          </div>
        )}
        <Text style={{ fontFamily: 'CustomFont' }} strong>
          افزودن نظر جدید
        </Text>
        <div style={{ marginTop: 8 }}>
          <MentionTextArea
            ref={mentionRef}
            value={commentContent}
            onChange={(newValue, mappings) => {
              setCommentContent(newValue);
              if (mappings) {
                setMentionMappings(mappings);
              }
            }}
            onSyncStatusChange={(isSyncing) => {
              setIsMentionSyncing(isSyncing);
            }}
            rows={4}
            maxLength={5000}
            placeholder="متن نظر خود را وارد کنید... (برای منشن کردن از @ استفاده کنید)"
          />
        </div>
        <div className="mt-3 flex justify-end">
          <Button
            type="primary"
            onClick={onSubmitComment}
            loading={commentSubmitting}
            disabled={isMentionSyncing}
            className={primaryButtonClass}
            style={{ borderRadius: 14, fontFamily: 'CustomFont' }}
          >
            {isMentionSyncing ? 'در حال همگام‌سازی...' : 'ثبت نظر'}
          </Button>
        </div>
      </div>
    </Card>
  );
}
